import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, X, Bot, Wallet, Banknote, Link as LinkIcon, Rocket, Coins, Clock } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { GET_STARTED_PAGE } from "../../mock";

const ICONS = { bot: Bot, wallet: Wallet, banknote: Banknote, link: LinkIcon, rocket: Rocket, coins: Coins };

function gridClass(count) {
  if (count === 1) return "mx-auto max-w-2xl grid-cols-1";
  if (count === 2) return "sm:grid-cols-2";
  if (count === 4) return "sm:grid-cols-2";
  return "sm:grid-cols-2 lg:grid-cols-3";
}

function VideoTile({ video, onOpen }) {
  const available = Boolean(video.youtubeId);
  return (
    <button
      type="button"
      onClick={() => available && onOpen(video)}
      disabled={!available}
      data-testid={`video-tile-${video.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
      className={`video-tile group block w-full text-left rounded-xl overflow-hidden card-dark ${available ? "btn-lift cursor-pointer" : "cursor-default"}`}
    >
      <div
        className="relative"
        style={{ position: "relative", paddingTop: "56.25%", background: "linear-gradient(135deg,#0b1030 0%,#14205a 60%,#1f6fb0 160%)" }}
      >
        {video.thumbnail && (
          <img
            src={video.thumbnail}
            alt={video.title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <div
          style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(180deg,rgba(5,8,25,0.15),rgba(5,8,25,0.4))",
          }}
        />
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {available ? (
            <span
              className="h-16 w-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
              style={{ background: "rgba(255,255,255,0.95)", boxShadow: "0 10px 30px rgba(0,0,0,0.45)" }}
            >
              <Play className="h-7 w-7 ml-1" style={{ color: "#0b1030" }} fill="#0b1030" />
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono-label text-[11px]"
              style={{ background: "rgba(0,0,0,0.5)", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", textTransform: "none" }}
            >
              <Clock className="h-3.5 w-3.5" /> Coming Soon
            </span>
          )}
        </div>
      </div>
      <p className="px-4 py-3 text-base font-semibold" style={{ color: "#141034" }}>{video.title}</p>
    </button>
  );
}

function VideoLightbox({ video, onClose }) {
  useEffect(() => {
    document.body.classList.add("no-scroll");
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{ alignItems: "center" }}
      data-testid="video-lightbox"
    >
      <div
        className="modal-panel w-full max-w-4xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-12 right-0 h-10 w-10 rounded-full flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.9)", color: "#0b1030" }}
          data-testid="lightbox-close"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="rounded-xl overflow-hidden" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}>
          <div style={{ position: "relative", paddingTop: "56.25%", background: "#000" }}>
            <iframe
              src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          </div>
        </div>
        <p className="mt-3 text-center font-display text-lg" style={{ color: "#fff" }}>{video.title}</p>
      </div>
    </div>,
    document.body
  );
}

export default function GetStartedPage() {
  const { eyebrow, heading, sub, steps, needHelp } = GET_STARTED_PAGE;
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="page-bg min-h-screen relative overflow-x-hidden" data-testid="get-started-page">
      <Header />
      <main className="relative z-10 pt-32 md:pt-36 pb-10">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold mb-8"
            style={{ color: "#03ACEF" }}
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>

          <div className="text-center">
            <div className="inline-flex items-center gap-3">
              <span className="h-px w-10" style={{ background: "rgba(3,172,239,0.45)" }} />
              <span className="font-mono-label section-eyebrow home-eyebrow" style={{ color: "#E8C97A" }}>{eyebrow}</span>
              <span className="h-px w-10" style={{ background: "rgba(3,172,239,0.45)" }} />
            </div>
            <h1
              className="font-display mt-3 leading-[1.05]"
              style={{ fontSize: "clamp(2rem,4.5vw,3.2rem)", color: "#fff" }}
              data-testid="get-started-heading"
            >
              {heading}
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg" style={{ color: "rgba(255,255,255,0.7)" }}>
              {sub}
            </p>
            <a
              href="mailto:eazybot@shyftasap.com?subject=EazyBot%20Setup%20Assistance"
              className="setup-email-button btn-lift mt-6 inline-flex max-w-full items-center justify-center rounded-full px-4 py-3.5 text-center text-sm font-semibold sm:px-7"
              data-testid="setup-email-button"
            >
              Email us: EazyBot@SHYFTASAP.com
            </a>
          </div>

          <div className="mt-12 space-y-6">
            {steps.map((step) => {
              const Icon = ICONS[step.icon] || Bot;
              return (
                <div key={step.no} className="rounded-2xl card-step p-6 md:p-8" data-testid={`step-${step.no}`}>
                  <div className="flex gap-5 md:gap-6">
                    <div className="shrink-0 flex items-start justify-center pt-1">
                      {step.icon === "bot" ? (
                        <span
                          className="h-20 w-20 rounded-2xl flex items-center justify-center overflow-hidden"
                          style={{ background: "rgba(3,172,239,0.12)" }}
                        >
                          <img src="/eazybot-helmet.png" alt="EazyBot" className="h-[74px] w-[74px] object-contain" />
                        </span>
                      ) : step.icon === "wallet" ? (
                        <div className="flex flex-col items-center gap-2">
                          <img
                            src="/coinbase.png"
                            alt="Coinbase"
                            className="h-14 w-14 rounded-xl object-cover"
                            style={{ boxShadow: "0 4px 14px rgba(0,0,0,0.3)" }}
                            data-testid="step-02-coinbase-icon"
                          />
                          <img
                            src="/kraken.jpg"
                            alt="Kraken"
                            className="h-14 w-14 rounded-xl object-cover"
                            style={{ boxShadow: "0 4px 14px rgba(0,0,0,0.3)" }}
                            data-testid="step-02-kraken-icon"
                          />
                        </div>
                      ) : (
                        <span
                          className="h-16 w-16 rounded-2xl flex items-center justify-center"
                          style={{ background: "rgba(3,172,239,0.12)", color: "#03ACEF" }}
                        >
                          <Icon className="h-8 w-8" />
                        </span>
                      )}
                    </div>

                    <div className="flex-1">
                      <p className="font-mono-label section-eyebrow" style={{ color: "#0369A1" }}>Step {step.no}</p>
                      <h2
                        className="font-display text-xl md:text-2xl mt-0.5"
                        style={{ color: "#141034" }}
                        data-testid={`step-${step.no}-title`}
                      >
                        {step.title}
                      </h2>
                      {step.note && (
                        <p className="mt-2 text-sm md:text-base italic leading-relaxed" style={{ color: "rgba(20,16,52,0.75)" }}>
                          {step.note}
                        </p>
                      )}
                      <p className="mt-2 text-sm md:text-base leading-relaxed" style={{ color: "rgba(20,16,52,0.75)" }}>
                        {step.body}
                      </p>
                      {step.body2 && (
                        <p className={`mt-2 text-sm md:text-base leading-relaxed ${step.body2Italic ? "italic" : ""}`} style={{ color: "rgba(20,16,52,0.75)" }}>
                          {step.body2}
                        </p>
                      )}
                    </div>
                  </div>

                  {step.videos && step.videos.length > 0 && (
                    <div className={`mt-6 grid gap-4 ${gridClass(step.videos.length)}`}>
                      {step.videos.map((v, i) => (
                        <VideoTile key={i} video={v} onOpen={setActiveVideo} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Need Help CTA */}
          <div
            className="mt-10 rounded-2xl p-8 md:p-10 text-center card-step"
            data-testid="need-help-section"
          >
            <div className="flex items-center justify-center gap-3">
              <span className="h-px w-10" style={{ background: "linear-gradient(90deg, transparent, rgba(3,172,239,0.65), transparent)" }} aria-hidden="true" />
              <p
                className="font-mono-label section-eyebrow"
                style={{ color: "#03ACEF", fontSize: "clamp(1rem,1.6vw,1.25rem)", letterSpacing: "0.22em" }}
              >
                {needHelp.eyebrow}
              </p>
              <span className="h-px w-10" style={{ background: "linear-gradient(90deg, transparent, rgba(3,172,239,0.65), transparent)" }} aria-hidden="true" />
            </div>
            <h2 className="font-display mt-3 text-2xl md:text-3xl" style={{ color: "#141034" }}>
              {needHelp.heading}
            </h2>
            <p className="mt-3 max-w-xl mx-auto text-sm md:text-base" style={{ color: "rgba(20,16,52,0.72)" }}>
              {needHelp.body}
            </p>
            <a
              href={needHelp.cta.href}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lift mt-6 inline-flex items-center gap-2 px-6 py-3.5 text-sm font-semibold rounded-full"
              style={{ background: "#03ACEF", color: "#141034" }}
              data-testid="need-help-cta"
            >
              {needHelp.cta.label}<span aria-hidden>&rarr;</span>
            </a>
          </div>
        </div>
      </main>
      <Footer />
      {activeVideo && (
        <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />
      )}
    </div>
  );
}
